import { useState } from 'react';
import { Table, Badge, Group, Text, Stack, Alert, Button, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconChecklist, IconCalendarTime } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SectionCard } from '../SectionCard';
import { EmptyState } from '../EmptyState';
import { TableSkeleton } from '../TableSkeleton';
import { FetchOverlay } from '../FetchOverlay';
import { SILENT_ERROR } from '../../lib/queryMeta';
import { fmtAvg, gradeColor, shortDate, type JournalCell, type JournalResponse } from '../../lib/journal';
import { GradeCellModal } from './GradeCellModal';

interface Props {
  classId: string | null;
  subjectId: string | null;
  /** IXTIYORIY (§6.2): berilmasa backend bugungi chorakni oladi. */
  termId: string | null;
  /** Choraklarni sozlash sahifasiga o'tish (bo'sh tizim yo'riqnomasi uchun). */
  onGoToTerms?: () => void;
  /** Sana filtri — faqat ko'rsatishga ta'sir qiladi (taklif chorak bo'yicha qoladi). */
  from?: string;
  to?: string;
}

/** Birinchi ustun gorizontal aylantirishda joyida turadi (ism doim ko'rinsin). */
const stickyCol: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: 'var(--mantine-color-body)',
};
const stickyHead: React.CSSProperties = { ...stickyCol, zIndex: 4 };

/**
 * Jurnal matritsasi (`GET /grades/journal`, §6.2):
 * qatorlar — o'quvchilar, ustunlar — sanalar, katakda o'sha kundagi baholar,
 * o'ng chetda o'rtacha va chorak bahosi.
 *
 * KENGLIK: gorizontal aylantirish FAQAT `Table.ScrollContainer` ichida bo'ladi
 * (`overflow-x: auto`), sahifa tanasi qimirlamaydi.
 * STICKY: `stickyHeader` bu loyihada 9 ta jadvalda TA'SIRSIZ, chunki `maxHeight`
 * berilmagan — shu sabab bu yerda `maxHeight` ATAYLAB berilgan, aks holda
 * yopishqoq sarlavha jimgina ishlamas edi.
 */
export function JournalMatrix({ classId, subjectId, termId, from, to, onGoToTerms }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Katakning O'ZI emas, faqat KOORDINATASI saqlanadi: baholar ro'yxati har renderda
  // yangi javobdan olinadi, aks holda modal ichida o'chirilgan/qo'shilgan bahodan keyin
  // eski ro'yxat qotib qolardi.
  const [cell, setCell] = useState<{ studentId: string; fullName: string; date: string } | null>(null);

  // §6.2: `termId` ixtiyoriy — sinf va fan yetarli.
  const ready = !!classId && !!subjectId;
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['grades-journal', classId, subjectId, termId, from, to],
    queryFn: async () =>
      (await api.get<JournalResponse>('/grades/journal', {
        /* `from`/`to` faqat KO'RINADIGAN ustunlarni kesadi. O'rtacha va taklif
           serverda butun chorak bo'yicha qoladi — shuning uchun filtr
           o'zgarganda ham taklif o'zgarmaydi. */
        params: { classId, subjectId, termId: termId || undefined, from, to },
      })).data,
    enabled: ready,
    meta: SILENT_ERROR,
  });

  const refresh = () => {
    void refetch();
    qc.invalidateQueries({ queryKey: ['grades'] });
    qc.invalidateQueries({ queryKey: ['term-grades'] });
    qc.invalidateQueries({ queryKey: ['grades-report'] });
  };

  const dates = data?.dates ?? [];
  const students = data?.students ?? [];
  const openCell = cell
    ? { ...cell, grades: (students.find((s) => s.id === cell.studentId)?.grades?.[cell.date] ?? []) as JournalCell[] }
    : null;
  // Ism (220) + har sana (72) + o'rtacha (100) + chorak bahosi (140)
  const minWidth = 220 + dates.length * 72 + 240;
  // Chorak topilmasa backend `term: null` qaytaradi (400 EMAS) — bu bo'sh tizimning
  // normal holati, xato emas: aynan shuni ayta oladigan bo'sh holat ko'rsatiladi.
  const noTerm = !!data && !data.term;

  return (
    <>
      <SectionCard
        title={t('journal.title')}
        icon={IconChecklist}
        subtitle={data?.term?.name}
        p={0}
        pos="relative"
      >
        <FetchOverlay visible={isFetching && !isLoading} />
        {!ready ? (
          <EmptyState icon={IconChecklist} title={t('journal.selectFilters')} description={t('journal.selectFiltersDesc')} />
        ) : isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : isError ? (
          <Stack align="center" gap="sm" p="lg">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
              {t('journal.loadError')}
            </Alert>
            <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
              {t('terms.retry')}
            </Button>
          </Stack>
        ) : noTerm ? (
          <EmptyState
            icon={IconCalendarTime}
            title={t('journal.noTerms')}
            description={t('journal.noTermsDesc')}
            action={onGoToTerms ? (
              <Button mt="xs" variant="light" leftSection={<IconCalendarTime size={16} />} onClick={onGoToTerms}>
                {t('journal.goToTerms')}
              </Button>
            ) : undefined}
          />
        ) : !students.length ? (
          <EmptyState icon={IconChecklist} title={t('journal.noStudents')} description={t('journal.noStudentsDesc')} />
        ) : !dates.length ? (
          <EmptyState icon={IconChecklist} title={t('journal.empty')} description={t('journal.emptyDesc')} />
        ) : (
          <>
            <Text c="dimmed" fz="xs" lh={1.5} px="md" pt="xs">{t('journal.scrollHint')}</Text>
            {/* `maxHeight` — `stickyHeader` ishlashi uchun SHART */}
            <Table.ScrollContainer minWidth={minWidth} maxHeight={560} type="native">
              <Table highlightOnHover withColumnBorders verticalSpacing="xs" stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={stickyHead} w={220}>{t('journal.student')}</Table.Th>
                    {dates.map((d) => (
                      <Table.Th key={d} ta="center" w={72}>{shortDate(d)}</Table.Th>
                    ))}
                    {/* JURNAL TABIDA DAVR KO'RSATKICHI — chorak emas.
                        O'qituvchi bu yerda "shu haftada bu o'quvchi qanday?"
                        degan savolga javob izlaydi; chorak o'rtachasi unga javob
                        bermaydi va tanlangan hafta bilan chalkashardi. Chorak
                        bahosi "Chorak yakuni" tabida qoladi. */}
                    <Table.Th ta="center" w={110}>{t('journal.periodAvg')}</Table.Th>
                    <Table.Th ta="center" w={120}>{t('journal.periodGrade')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {students.map((s) => {
                    return (
                      <Table.Tr key={s.id}>
                        <Table.Td style={stickyCol} fw={500}>{s.fullName}</Table.Td>
                        {dates.map((d) => {
                          const list = s.grades?.[d] ?? [];
                          return (
                            <Table.Td key={d} p={4}>
                              <UnstyledButton
                                w="100%"
                                onClick={() => setCell({ studentId: s.id, fullName: s.fullName, date: d })}
                                aria-label={`${s.fullName} · ${d}`}
                                style={{ display: 'block', borderRadius: 6, padding: 2, textAlign: 'center' }}
                              >
                                {list.length ? (
                                  <Group gap={3} justify="center" wrap="nowrap">
                                    {list.map((g) => (
                                      <Badge key={g.id} size="sm" color={gradeColor(g.value)} circle>{g.value}</Badge>
                                    ))}
                                  </Group>
                                ) : (
                                  <Text c="dimmed" fz="sm" lh={1.4}>·</Text>
                                )}
                              </UnstyledButton>
                            </Table.Td>
                          );
                        })}
                        <Table.Td ta="center" fw={600}>
                          {fmtAvg(s.periodAvg ?? null)}
                          {!!s.periodCount && <Text span c="dimmed" fz="xs"> ({s.periodCount})</Text>}
                        </Table.Td>
                        <Table.Td ta="center">
                          {s.periodSuggested != null ? (
                            <Badge size="lg" color={gradeColor(s.periodSuggested)}>{s.periodSuggested}</Badge>
                          ) : (
                            /* Davrda baho yo'q — bu "baho qo'yilmagan" degani,
                               "2" degani EMAS. Chiziqcha shuni ajratadi. */
                            <Text c="dimmed" fz="sm" lh={1.4}>—</Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}
      </SectionCard>

      <GradeCellModal
        cell={openCell}
        subjectId={subjectId ?? ''}
        termId={data?.term?.id ?? termId ?? ''}
        onClose={() => setCell(null)}
        onChanged={refresh}
      />
    </>
  );
}
